/**
 * components/Button/types.ts
 * The button vocabulary, derived from every button that exists in the design.
 */

/**
 * SIZE — the design uses six button heights and no others. Each height comes
 * with its own horizontal padding and font size; they are not independent
 * choices, so they travel together in SIZE_SPEC.
 *
 *   xs    26px  card-header and inline chips ("View ledger")
 *   sm    27px  card-header actions ("Add rule", "Worst first")
 *   md    29px  table-row actions, modal chip rows ("Reject", "Approve")
 *   lg    30px  the header primary ("＋ Assign cash"), filter chips, search
 *   modal 31px  modal footer ("Cancel", the primary)
 *   xl    34px  drawer actions and the onboarding primary
 */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'modal' | 'xl';

/**
 * VARIANT
 *   primary  ink fill, white text — the one action that completes the flow
 *   outline  white fill, #E4E4E4 border — everything else
 *   danger   the light-red family — Reject, and only Reject
 *   drawer   #D8D8D8 border, 6px radius, left-aligned label
 *
 * `drawer` exists because the ledger drawer's seven actions are deliberately
 * EQUAL WEIGHT: a single filled button in a row of outlined ones reads as a
 * selected filter rather than an action, so the drawer has no primary at all.
 */
export type ButtonVariant = 'primary' | 'outline' | 'danger' | 'drawer';

export interface SizeSpec {
  height: number;
  /** Horizontal padding for a filled button. */
  paddingPrimary: number;
  /** Horizontal padding for an outlined button — one pixel tighter. */
  paddingOutline: number;
  fontSize: number;
}

/** Every value here is read straight out of the design source. */
export const SIZE_SPEC: Record<ButtonSize, SizeSpec> = {
  xs: { height: 26, paddingPrimary: 10, paddingOutline: 10, fontSize: 11.5 },
  sm: { height: 27, paddingPrimary: 11, paddingOutline: 11, fontSize: 12 },
  md: { height: 29, paddingPrimary: 12, paddingOutline: 11, fontSize: 12 },
  lg: { height: 30, paddingPrimary: 13, paddingOutline: 13, fontSize: 12.5 },
  modal: { height: 31, paddingPrimary: 14, paddingOutline: 13, fontSize: 12.5 },
  xl: { height: 34, paddingPrimary: 16, paddingOutline: 14, fontSize: 13 },
};
